import { Helmet } from 'react-helmet-async';
import PageHeader from './PageHeader';
import { Box, Grid, styled } from '@mui/material';
import Results from './Results';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'src/store';
import SuspenseLoader from 'src/components/SuspenseLoader';
import { getAllEvents } from 'src/slices/events/eventAction';
import { setActiveEvent } from 'src/slices/events/eventSlice';

const MainContentWrapper = styled(Box)(() => 
  `
    flex-grow: 1;
  `
);

function StatisticsBoleteria() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [initialized, setInitialized] = useState(false);
  const [hasRequestedEvents, setHasRequestedEvents] = useState(false);
  const eventId = searchParams.get('eventId') || '';
  const { activeEvent, events, isLoading } = useSelector((state) => state.events);
  const foundEvent = useMemo(
    () => (eventId ? events?.find((event) => event.id === eventId) : null),
    [eventId, events]
  );

  const { t }: { t: any } = useTranslation();

  useEffect(() => {
    setInitialized(false);
    setHasRequestedEvents(false);
  }, [eventId]);

  useEffect(() => {
    if (!eventId) {
      setInitialized(true);
      return;
    }

    if (activeEvent?.id === eventId) {
      setInitialized(true);
      return;
    }

    if (!foundEvent && !hasRequestedEvents) {
      setHasRequestedEvents(true);
      dispatch(getAllEvents(false) as any);
      return;
    }

    if (foundEvent) {
      dispatch(setActiveEvent(foundEvent));
    }

    setInitialized(true);
  }, [
    activeEvent?.id,
    dispatch,
    eventId,
    events,
    foundEvent,
    hasRequestedEvents
  ]);

  useEffect(() => {
    if (eventId || !activeEvent?.id) return;
    const params = new URLSearchParams(searchParams);
    params.set('eventId', activeEvent.id);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [activeEvent?.id, eventId, location.pathname, navigate, searchParams]);

  useEffect(() => {
    if (!initialized) return;

    if (!eventId && !activeEvent) {
      navigate('/dashboard/management/events/list', { replace: true });
      return;
    }

    const isInvalidEventId = Boolean(
      eventId &&
      !isLoading &&
      hasRequestedEvents &&
      !foundEvent &&
      activeEvent?.id !== eventId
    );

    if (isInvalidEventId) {
      navigate('/dashboard/management/events/list', { replace: true });
    }
  }, [
    activeEvent,
    eventId,
    foundEvent,
    hasRequestedEvents,
    initialized,
    isLoading,
    navigate
  ]);

  const shouldShowLoader = Boolean(
    eventId &&
    (!initialized || activeEvent?.id !== eventId)
  );

  if (shouldShowLoader || !activeEvent) {
    return <SuspenseLoader />;
  }

  return (
    <>
      <Helmet>
        <title>{t("Statistics by event")}</title>
      </Helmet>
      <Box mb={3} display="flex">
        <MainContentWrapper>
          <Grid sx={{ px: 4 }} container direction="row" justifyContent="center" alignItems="stretch" spacing={4}>
            <Grid item xs={12}>
              <Box mt={3} display="flex" alignItems="center" justifyContent="space-between">
                <PageHeader />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Results />
            </Grid>
          </Grid>
        </MainContentWrapper>
      </Box>
    </>
  );
}

export default StatisticsBoleteria;

